export type SoundCue = "collect" | "combo" | "hazard" | "lowTime" | "results" | "resultsWin";

type ToneProfile = {
  frequency: number;
  duration: number;
  type: OscillatorType;
  pitchRatio: number;
  peakGain: number;
};

const PROFILES: Record<SoundCue, ToneProfile> = {
  collect: { frequency: 620, duration: 0.045, type: "sine", pitchRatio: 1.35, peakGain: 0.055 },
  combo: { frequency: 780, duration: 0.09, type: "triangle", pitchRatio: 1.5, peakGain: 0.06 },
  hazard: { frequency: 130, duration: 0.14, type: "sawtooth", pitchRatio: 0.5, peakGain: 0.07 },
  lowTime: { frequency: 260, duration: 0.06, type: "square", pitchRatio: 1.35, peakGain: 0.05 },
  results: { frequency: 360, duration: 0.22, type: "sine", pitchRatio: 0.75, peakGain: 0.06 },
  resultsWin: { frequency: 500, duration: 0.16, type: "triangle", pitchRatio: 1.4, peakGain: 0.065 },
};

// Rising/falling note sequences layered on top of the base tone for cues that
// should feel like a musical phrase rather than a single beep - relative
// semitone offsets from the profile's base frequency, each with a start delay.
const SEQUENCES: Partial<Record<SoundCue, Array<{ semitones: number; delay: number }>>> = {
  combo: [{ semitones: 0, delay: 0 }, { semitones: 7, delay: 0.055 }],
  resultsWin: [
    { semitones: 0, delay: 0 },
    { semitones: 4, delay: 0.09 },
    { semitones: 7, delay: 0.18 },
  ],
};

const semitoneRatio = (semitones: number): number => 2 ** (semitones / 12);

// Sample-based audio, dropped into public/audio/ (see public/audio/README.md).
// Every entry here is optional: a missing or failed-to-load file just means
// that cue keeps using the procedural synth above. Nothing needs to change
// in code once real files are added - they're picked up automatically.
const SAMPLE_FILES: Record<SoundCue, string> = {
  collect: "collect.mp3",
  combo: "combo.mp3",
  hazard: "hazard.mp3",
  lowTime: "low-time.mp3",
  results: "results-lose.mp3",
  resultsWin: "results-win.mp3",
};
const MUSIC_LOOP_FILE = "music-loop.mp3";
const MUSIC_GAIN = 0.32;

export class AudioManager {
  private context?: AudioContext;
  private noiseBuffer?: AudioBuffer;
  private lastLowTimeCue = 0;
  private samples = new Map<string, AudioBuffer | null>();
  private samplesLoading = false;
  private musicSource?: AudioBufferSourceNode;
  private musicGain?: GainNode;

  constructor(private enabled: boolean, private assetsBase = "./") {}

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (enabled) {
      this.playMusicLoop();
    } else {
      this.stopMusicLoop();
    }
  }

  play(cue: SoundCue): void {
    if (!this.enabled || typeof AudioContext === "undefined") return;
    const now = performance.now();
    if (cue === "lowTime" && now - this.lastLowTimeCue < 650) return;
    if (cue === "lowTime") this.lastLowTimeCue = now;

    const context = this.context ??= new AudioContext();
    if (context.state === "suspended") void context.resume();
    this.preloadSamples(context);

    const sample = this.samples.get(SAMPLE_FILES[cue]);
    if (sample) {
      this.playSample(context, sample, 0.7);
      return;
    }

    const profile = PROFILES[cue];
    const notes = SEQUENCES[cue] ?? [{ semitones: 0, delay: 0 }];
    for (const note of notes) {
      this.playTone(context, profile, note.semitones, note.delay);
    }
    if (cue === "hazard") this.playImpactNoise(context);
  }

  /** Starts the looping background track if public/audio/music-loop.* has been provided. */
  playMusicLoop(): void {
    if (!this.enabled || typeof AudioContext === "undefined" || this.musicSource) return;
    const context = this.context ??= new AudioContext();
    this.preloadSamples(context);
    const buffer = this.samples.get(MUSIC_LOOP_FILE);
    if (!buffer) return;
    if (context.state === "suspended") void context.resume();

    const source = context.createBufferSource();
    const gain = context.createGain();
    source.buffer = buffer;
    source.loop = true;
    gain.gain.value = MUSIC_GAIN;
    source.connect(gain);
    gain.connect(context.destination);
    source.start();
    this.musicSource = source;
    this.musicGain = gain;
  }

  stopMusicLoop(): void {
    const source = this.musicSource;
    const gain = this.musicGain;
    if (source && gain && this.context) {
      const stopAt = this.context.currentTime + 0.12;
      gain.gain.exponentialRampToValueAtTime(0.0001, stopAt);
      source.stop(stopAt);
    } else {
      source?.stop();
    }
    this.musicSource = undefined;
    this.musicGain = undefined;
  }

  private preloadSamples(context: AudioContext): void {
    if (this.samplesLoading) return;
    this.samplesLoading = true;
    const files = [...new Set([...Object.values(SAMPLE_FILES), MUSIC_LOOP_FILE])];
    for (const file of files) {
      this.loadSample(context, file)
        .then((buffer) => {
          this.samples.set(file, buffer);
          if (file === MUSIC_LOOP_FILE && buffer) this.playMusicLoop();
        })
        .catch(() => this.samples.set(file, null));
    }
  }

  private async loadSample(context: AudioContext, file: string): Promise<AudioBuffer | null> {
    try {
      const response = await fetch(`${this.assetsBase}audio/${file}`);
      if (!response.ok) return null;
      const data = await response.arrayBuffer();
      return await context.decodeAudioData(data);
    } catch {
      return null;
    }
  }

  private playSample(context: AudioContext, buffer: AudioBuffer, peakGain: number): void {
    const source = context.createBufferSource();
    const gain = context.createGain();
    const startAt = context.currentTime;
    source.buffer = buffer;
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.exponentialRampToValueAtTime(peakGain, startAt + 0.008);
    source.connect(gain);
    gain.connect(context.destination);
    source.start(startAt);
  }

  private playTone(context: AudioContext, profile: ToneProfile, semitones: number, delay: number): void {
    const startAt = context.currentTime + delay;
    const frequency = profile.frequency * semitoneRatio(semitones);

    const oscillator = context.createOscillator();
    const detuned = context.createOscillator();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();

    oscillator.type = profile.type;
    detuned.type = profile.type;
    oscillator.frequency.setValueAtTime(frequency, startAt);
    detuned.frequency.setValueAtTime(frequency * 1.006, startAt);
    for (const osc of [oscillator, detuned]) {
      osc.frequency.exponentialRampToValueAtTime(
        Math.max(40, frequency * profile.pitchRatio),
        startAt + profile.duration,
      );
    }

    filter.type = "lowpass";
    filter.frequency.setValueAtTime(Math.min(9000, frequency * 6), startAt);
    filter.Q.value = 0.7;

    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.exponentialRampToValueAtTime(profile.peakGain, startAt + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + profile.duration);

    oscillator.connect(filter);
    detuned.connect(filter);
    filter.connect(gain);
    gain.connect(context.destination);
    oscillator.start(startAt);
    detuned.start(startAt);
    oscillator.stop(startAt + profile.duration + 0.02);
    detuned.stop(startAt + profile.duration + 0.02);
  }

  private playImpactNoise(context: AudioContext): void {
    const buffer = this.noiseBuffer ??= this.createNoiseBuffer(context);
    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const gain = context.createGain();

    source.buffer = buffer;
    filter.type = "bandpass";
    filter.frequency.value = 900;
    filter.Q.value = 0.9;

    const startAt = context.currentTime;
    const duration = 0.09;
    gain.gain.setValueAtTime(0.09, startAt);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

    source.connect(filter);
    filter.connect(gain);
    gain.connect(context.destination);
    source.start(startAt);
    source.stop(startAt + duration);
  }

  private createNoiseBuffer(context: AudioContext): AudioBuffer {
    const length = Math.round(context.sampleRate * 0.12);
    const buffer = context.createBuffer(1, length, context.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < length; i += 1) {
      data[i] = Math.random() * 2 - 1;
    }
    return buffer;
  }
}
